/**
 * useNotification Hook
 *
 * Provides a consistent API for showing toast notifications throughout the app.
 * Replaces all alert() calls with proper toast notifications.
 *
 * @see .github/copilot-instructions.md#components--ui UI patterns
 *
 * Usage:
 * ```tsx
 * const { showSuccess, showError, showInfo, showWarning } = useNotification();
 *
 * showSuccess("Operation completed!");
 * showError("Failed to save data");
 *
 * // With action button
 * showWarning("Session expired", {
 *   persist: true,
 *   action: <Button onClick={handleLogout}>Log Out</Button>
 * });
 * ```
 */

import { useSnackbar, VariantType, SnackbarKey } from 'notistack';
import { useCallback } from 'react';
import type { ReactNode } from 'react';

export interface NotificationOptions {
  variant?: VariantType;
  autoHideDuration?: number;
  persist?: boolean;
  action?: ReactNode | ((key: SnackbarKey) => ReactNode);
}

/**
 * Normalize options parameter - handles both number (autoHideDuration) and object formats
 */
const normalizeOptions = (
  options?: number | Omit<NotificationOptions, 'variant'>,
): Omit<NotificationOptions, 'variant'> | undefined => {
  return typeof options === 'number' ? { autoHideDuration: options } : options;
};

export const useNotification = () => {
  const { enqueueSnackbar } = useSnackbar();

  const showNotification = useCallback(
    (message: string, options?: NotificationOptions) => {
      enqueueSnackbar(message, {
        variant: options?.variant || 'default',
        autoHideDuration: options?.autoHideDuration,
        persist: options?.persist,
        action: options?.action,
      });
    },
    [enqueueSnackbar],
  );

  const showSuccess = useCallback(
    (message: string, options?: number | Omit<NotificationOptions, 'variant'>) => {
      showNotification(message, { variant: 'success', ...normalizeOptions(options) });
    },
    [showNotification],
  );

  const showError = useCallback(
    (message: string, options?: number | Omit<NotificationOptions, 'variant'>) => {
      showNotification(message, { variant: 'error', ...normalizeOptions(options) });
    },
    [showNotification],
  );

  const showWarning = useCallback(
    (message: string, options?: number | Omit<NotificationOptions, 'variant'>) => {
      showNotification(message, { variant: 'warning', ...normalizeOptions(options) });
    },
    [showNotification],
  );

  const showInfo = useCallback(
    (message: string, options?: number | Omit<NotificationOptions, 'variant'>) => {
      showNotification(message, { variant: 'info', ...normalizeOptions(options) });
    },
    [showNotification],
  );

  return {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};
